import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { toast } from 'sonner';
import { apiService } from '../../../utils/api';
import { getApiErrorMessage } from '../../../utils/apiError';
import {
  createApprover,
  createExemption,
  deleteApprover,
  deleteExemption,
  listApprovers,
  listExemptions,
  toggleApproval,
} from '../api';
import { APPROVAL_CONTEXT_KEY } from './useApprovals';
import type { ApprovalContext } from '../types';

const APPROVERS_KEY = 'approvers';
const EXEMPTIONS_KEY = 'approval-exemptions';

export const useApprovers = () => {
  const companyId = useSelector((state: any) => state.auth.current_company);
  return useQuery({
    queryKey: [APPROVERS_KEY, companyId],
    queryFn: listApprovers,
    refetchOnWindowFocus: false,
  });
};

export const useExemptions = () => {
  const companyId = useSelector((state: any) => state.auth.current_company);
  return useQuery({
    queryKey: [EXEMPTIONS_KEY, companyId],
    queryFn: listExemptions,
    refetchOnWindowFocus: false,
  });
};

/** Zaměstnanci firmy pro výběr schvalovatele (stejný zdroj jako správa firmy). */
export const useCompanyEmployees = (search?: string) => {
  const companyId = useSelector((state: any) => state.auth.current_company);
  return useQuery({
    queryKey: ['employee-left-panel', companyId, search],
    queryFn: async () => {
      const { data } = await apiService.get('/api/v1/employee/left-panel', {
        params: { page: 1, page_size: 100, search: search || undefined },
      });
      return data?.results ?? [];
    },
    refetchOnWindowFocus: false,
  });
};

export const useToggleApproval = (companyId?: string) => {
  const dispatch = useDispatch();
  const store = useStore<any>();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => toggleApproval(companyId as string, enabled),
    onSuccess: ({ data }) => {
      // A late response must not change the newly selected company's settings.
      if (String(store.getState().auth.current_company) !== String(companyId)) return;
      dispatch({
        type: 'COMPANY_EDITED_SUCCESSFULL',
        data: {
          solution_approval_required: data.solution_approval_required,
        },
      });
      // The settings switch and the Data › Schvalování menu read the context,
      // so write the server value now (no flicker) and then refetch it.
      queryClient.setQueriesData<ApprovalContext | undefined>([APPROVAL_CONTEXT_KEY], (ctx) =>
        ctx ? { ...ctx, enabled: data.solution_approval_required } : ctx
      );
      queryClient.invalidateQueries([APPROVAL_CONTEXT_KEY]);
      toast.success(
        data.solution_approval_required ? 'Schvalování zapnuto' : 'Schvalování vypnuto'
      );
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
};

export const useCreateApprover = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createApprover,
    onSuccess: () => {
      toast.success('Schvalovatel přidán');
      queryClient.invalidateQueries([APPROVERS_KEY]);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
};

export const useDeleteApprover = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteApprover,
    onSuccess: () => {
      toast.success('Schvalovatel odebrán');
      queryClient.invalidateQueries([APPROVERS_KEY]);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
};

export const useCreateExemption = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createExemption,
    onSuccess: () => {
      toast.success('Výjimka přidána');
      queryClient.invalidateQueries([EXEMPTIONS_KEY]);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
};

export const useDeleteExemption = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteExemption,
    onSuccess: () => {
      toast.success('Výjimka zrušena');
      queryClient.invalidateQueries([EXEMPTIONS_KEY]);
    },
    onError: (err) => {
      toast.error(getApiErrorMessage(err));
    },
  });
};
